import Graphic from "@arcgis/core/Graphic";
import { getMapView } from "./arcgisUtils";
import WMSLayer from "@arcgis/core/layers/WMSLayer.js";
import esriRequest from "@arcgis/core/request.js";
import FieldInfo from "@arcgis/core/popup/FieldInfo.js";
import Layer from "@arcgis/core/layers/Layer";
import GroupLayer from "@arcgis/core/layers/GroupLayer";
import { LayerSetting, MapSettings } from "../types/types";
import Basemap from "@arcgis/core/Basemap";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";
import ImageryTileLayer from "@arcgis/core/layers/ImageryTileLayer.js";
import WebTileLayer from "@arcgis/core/layers/WebTileLayer";
import { getMapSettings } from "./localStorageUtils";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { FeatureLayerNFS, LayerNFS } from "../extensions/FeatureLayerNFS";
import type BasemapGalleryViewModel from "@arcgis/core/widgets/BasemapGallery/BasemapGalleryViewModel";
import type WebMap from "@arcgis/core/WebMap";

export const addWmsLayerToMapFromUrl = async (layerUrl: string, mapViewId: string) => {
    const mapview = getMapView(mapViewId);
    if (!mapview?.map) return;

    const wmsLayer = new WMSLayer({
        url: layerUrl,
        featureInfoFormat: "text/html",
    });

    await wmsLayer.load();
    wmsLayer.allSublayers.forEach((sublayer) => {
        sublayer.popupEnabled = true;
    });

    // TODO: Add support for multiple featureInfoFormat types
    wmsLayer.fetchFeatureInfoFunction = async (query) => {
        query.info_format = "application/json";
        query.feature_count = 5;

        let data: Array<Graphic> = [];

        await esriRequest(wmsLayer.featureInfoUrl ?? "", { query })
            .then((response: any) => {
                data = response.data.features.map(
                    (feature: any) =>
                        new Graphic({
                            attributes: feature.properties,
                            popupTemplate: {
                                content: [
                                    {
                                        type: "fields",
                                        fieldInfos: getGFIFieldInfos(feature.properties),
                                    },
                                ],
                            },
                            layer: wmsLayer,
                        })
                );
            })
            .catch((error: any) => {
                console.log(error);
            });

        return data;
    };

    mapview.map.add(wmsLayer);
};

export const removeWmsLayerFromMap = async (layerUrl: string, mapViewId: string) => {
    const mapview = getMapView(mapViewId);
    if (!mapview?.map) return;

    const layers = mapview.map.layers.toArray();
    const wmsLayer = layers.find((layer) => (layer as WMSLayer)?.url === layerUrl);

    if (wmsLayer) {
        mapview.map.remove(wmsLayer);
    }
};
export const AttrToExclude = [
    "OBJECTID",
    "GlobalId",
    "ESRI_OID",
    "Shape",
    "Shape__Area",
    "Shape__Length",
    "geometry",
    "gml:boundedBy",
    "objid",
    "Polygon_Id",
];
export const getGFIFieldInfos = (featureProps: any): Array<FieldInfo> => {
    let fInfos: Array<FieldInfo> = [];

    Object.keys(featureProps).forEach((p) => {
        if (!AttrToExclude.some((a) => p.toUpperCase().indexOf(a.toUpperCase()) !== -1)) {
            if (isValidStringContent(featureProps[p])) {
                fInfos.push(
                    new FieldInfo({
                        fieldName: p,
                        label: p,
                        visible: true,
                    })
                );
            }
        }
    });

    return fInfos;
};

export const isValidStringContent = (val: string): boolean => {
    if (val != null) {
        val = val.toString().trim();
    }
    return val !== "" && val !== null && val !== "Null";
};

export const tryToggleLayer = (layer: Layer, refreshConfigWebMap: () => void, onSettingsChanged?: () => void) => {
    const mapview = getMapView();
    if (!mapview?.map) return;

    const allLayers = mapview.map.allLayers.toArray();

    const layerInMap = allLayers.find((l) => l.title === layer.title);
    if (layerInMap) {
        layerInMap.visible = !layerInMap.visible;
        if (layerInMap.visible) {
            setAllParentsVisible(layerInMap);
        }
        // Auto-save settings after visibility change
        onSettingsChanged?.();
    } else {
        addUpmostParentToMap(layer).then(() => {
            setAllParentsVisible(layer);
            // Auto-save settings after adding layer
            onSettingsChanged?.();
        });
        refreshConfigWebMap();
    }
};

const setAllParentsVisible = (layer: Layer) => {
    if (layer.parent && layer.parent instanceof Layer) {
        layer.parent.visible = true;
        setAllParentsVisible(layer.parent as Layer);
    }
};

export const addUpmostParentToMap = async (layer: Layer) => {
    const mapview = getMapView();
    if (!mapview?.map) return;

    const allLayers = mapview.map.allLayers.toArray();

    // Find the path of titles from the layer to its topmost parent
    const findTitlePath = (currentLayer: Layer, path: string[] = []): string[] => {
        path.unshift(currentLayer.title!);

        // If no parent or parent is not a Layer, we've reached the top
        if (!currentLayer.parent || !(currentLayer.parent instanceof Layer)) {
            return path;
        }

        return findTitlePath(currentLayer.parent as Layer, path);
    };

    // Get the path of titles from layer to root
    const titlePath = findTitlePath(layer);

    // Find the topmost parent that isn't already in the map
    let topmostParentToAdd: Layer | undefined = layer;
    let topmostParentTitle: string | undefined;

    for (const title of titlePath) {
        const layerInMap = allLayers.find((l) => l.title === title);
        if (!layerInMap) {
            topmostParentTitle = title;
            break;
        }
    }

    // If all layers in the path are already in the map, nothing to do
    if (!topmostParentTitle) return;

    // Find the actual topmost parent layer object to add
    let currentLayer: Layer = layer;
    while (currentLayer.parent instanceof Layer && currentLayer.title !== topmostParentTitle) {
        currentLayer = currentLayer.parent;
    }

    // This is the layer we need to add to the map
    topmostParentToAdd = currentLayer;

    if (topmostParentToAdd) {
        // Ensure the layer and its sublayers are loaded
        // await topmostParentToAdd.load();

        // Filter down the layer tree based on titlePath
        // Skip the first title (index 0) since it's the topmostParentToAdd itself
        if (topmostParentToAdd instanceof GroupLayer && titlePath.length > 1) {
            for (let i = 1; i < titlePath.length; i++) {
                const targetTitle = titlePath[i];

                // Find the current level group layer
                let currentGroupLayer: GroupLayer;

                if (i === 1) {
                    currentGroupLayer = topmostParentToAdd;
                } else {
                    // Look for the group layer in the previous level
                    const sublayers = topmostParentToAdd.layers.toArray();
                    const targetLayer = sublayers.find((l) => l.visible);

                    if (targetLayer instanceof GroupLayer) {
                        currentGroupLayer = targetLayer;
                    } else {
                        break; // We've reached a non-group layer, stop the process
                    }
                }

                // Keep only the layer that matches the current path segment
                const layersToKeep = currentGroupLayer.layers.toArray().filter((l) => l.title === targetTitle);

                // Remove all other layers from this level
                const layersToRemove = currentGroupLayer.layers.toArray().filter((l) => l.title !== targetTitle);

                for (const layer of layersToRemove) {
                    currentGroupLayer.layers.remove(layer);
                }

                // Make sure the target layer is visible
                if (layersToKeep.length > 0) {
                    layersToKeep[0].visible = true;
                }
            }
        }

        // Add the layer to the map
        if (topmostParentTitle === titlePath[0]) {
            mapview.map.add(topmostParentToAdd);
        } else {
            const indexOfTitle = titlePath.indexOf(topmostParentTitle);
            const parentTitle = titlePath[indexOfTitle - 1];
            const parentLayer = allLayers.find((l) => l.title === parentTitle);
            if (parentLayer && parentLayer instanceof GroupLayer) {
                parentLayer.add(topmostParentToAdd);
            }
        }
    }
};

export const isLayerEnabledOrInMap = (layer: Layer): boolean => {
    const mapview = getMapView();
    if (!mapview?.map) return false;

    const allLayers = mapview.map.allLayers.toArray();
    const layerInMap = allLayers.find((l) => l.title === layer.title);

    if (layerInMap) {
        return layerInMap.visible;
    } else {
        return false;
    }
};

export const isNFSLayer = (layer: Layer): boolean =>
    (layer instanceof FeatureLayer && (layer as FeatureLayerNFS).isVersioned === true) ||
    (layer as LayerNFS).includeInNFSLayers === true;

export const addThemeLayersToMap = async (themeMap: WebMap) => {
    const mapview = getMapView();
    if (!mapview) return;

    const allLayers = themeMap.layers.toArray();

    hideLayersNotInThemeMap(themeMap);

    const allPromises = [];
    for (const layer of allLayers) {
        allPromises.push(addUpmostParentToMap(layer));
    }

    await Promise.all(allPromises);
    showLayersInThemeMap(themeMap);
};

const hideLayersNotInThemeMap = (themeMap: WebMap) => {
    const mapview = getMapView();
    if (!mapview?.map) return;

    const allLayers = mapview.map.layers.toArray();
    const themeLayers = themeMap.layers.toArray();

    allLayers.forEach((layer) => {
        if (!themeLayers.some((tl) => tl.title === layer.title)) {
            if (layer.type !== "graphics" && !isNFSLayer(layer)) {
                layer.visible = false;
            }
        }
    });
};

const showLayersInThemeMap = (themeMap: WebMap) => {
    const mapview = getMapView();
    if (!mapview?.map) return;

    const allLayers = mapview.map.layers.toArray();
    const themeLayers = themeMap.layers.toArray();

    allLayers.forEach((layer) => {
        if (themeLayers.some((tl) => tl.title === layer.title)) {
            layer.visible = true;
        }
    });
};

export const storeLayerInFavorites = (layer: Layer) => {
    const favorites = localStorage.getItem("layerFavorites") ?? "[]";
    const parsedFavorites = JSON.parse(favorites) as Array<{ title: string; url: string }>;

    // Get the layer URL if available
    const layerUrl = (layer as any).url ?? "";

    // Check if this layer is already in favorites
    const existingLayer = parsedFavorites.find(
        (f) => f.title === layer.title && ((!f.url && !layerUrl) || f.url === layerUrl)
    );

    if (!existingLayer) {
        if (layer.title) {
            parsedFavorites.push({
                title: layer.title,
                url: layerUrl,
            });
            localStorage.setItem("layerFavorites", JSON.stringify(parsedFavorites));
            globalThis.dispatchEvent(new Event("favoritesChanged"));
        }
    }
};

export const removeLayerFromFavorites = (layer: Layer) => {
    const favorites = localStorage.getItem("layerFavorites") ?? "[]";
    const parsedFavorites = JSON.parse(favorites) as Array<{ title: string; url: string }>;

    // Get the layer URL if available
    const layerUrl = (layer as any).url ?? "";

    // Remove the layer from favorites
    const updatedFavorites = parsedFavorites.filter((f) => !(f.title === layer.title && f.url === layerUrl));

    localStorage.setItem("layerFavorites", JSON.stringify(updatedFavorites));
    globalThis.dispatchEvent(new Event("favoritesChanged"));
};

export const isLayerFavorited = (layer: Layer): boolean => {
    const favorites = localStorage.getItem("layerFavorites") ?? "[]";
    const parsedFavorites = JSON.parse(favorites) as Array<{ title: string; url: string }>;

    // Get the layer URL if available
    const layerUrl = (layer as any).url ?? "";

    for (const f of parsedFavorites) {
        if (f.title === layer.title && layerUrl === f.url) {
            return true;
        }
    }
    return false;
};

export const getFavorites = (): { title: string; url: string }[] => {
    const favorites = localStorage.getItem("layerFavorites") ?? "[]";
    const parsedFavorites = JSON.parse(favorites) as Array<{ title: string; url: string }>;
    return parsedFavorites;
};

export const storeMapSettingsInLocalStorage = (enabledLabels: string[], labelsEnabled: boolean) => {
    const mapview = getMapView();
    if (!mapview?.map) return;

    const basemap = mapview.map.basemap;
    console.log(basemap);

    const baseLayers = basemap?.baseLayers.toArray() || [];
    const referenceLayers = basemap?.referenceLayers.toArray() || [];

    console.log("Base layers:", baseLayers);
    console.log("Reference layers:", referenceLayers);

    const basemapLayers: { layerType: string; type: string; url: string }[] = [];

    baseLayers.forEach((layer) => {
        basemapLayers.push({
            layerType: layer.type,
            type: "base",
            url: (layer as any).url || "",
        });
    });

    referenceLayers.forEach((layer) => {
        basemapLayers.push({
            layerType: layer.type,
            type: "reference",
            url: (layer as any).url || "",
        });
    });

    const enabledLayers: LayerSetting[] = [];

    // Helper function to recursively build layer settings
    const buildLayerSetting = (layer: Layer, parentSetting: LayerSetting | null = null): void => {
        if (!layer.visible || layer.listMode === "hide" || layer.type === "graphics") {
            return;
        }

        const layerUrl = (layer as any).url || "";
        const layerId = (layer as any).layerId ?? -1;
        const opacity = (layer as any).opacity ?? 1;
        const minScale = (layer as any).minScale ?? 0;
        const title = layer.title || "Untitled Layer";

        const layerSetting: LayerSetting = {
            parent: parentSetting,
            layerType: layer.type,
            url: layerUrl,
            title: title,
            layerId: layerId,
            opacity: opacity,
            zoomLevel: minScale,
        };

        // Add this layer to the enabled layers array
        enabledLayers.push(layerSetting);

        // If it's a GroupLayer, process its children recursively
        if (layer instanceof GroupLayer) {
            const children = layer.layers.toArray();
            for (const childLayer of children) {
                buildLayerSetting(childLayer, layerSetting);
            }
        }
    };

    // Get only top-level layers from the map
    const topLevelLayers = mapview.map.layers.toArray();

    topLevelLayers.forEach((layer) => {
        buildLayerSetting(layer, null);
    });

    const mapSettings: MapSettings = {
        basemap: basemapLayers,
        enabledLayers: enabledLayers,
        enabledLabels: enabledLabels,
        labelsEnabled: labelsEnabled,
    };

    localStorage.setItem("mapSettings", JSON.stringify(mapSettings));
};

export const loadMapSettingsFromLocalStorage = async (
    themeMaps: WebMap[],
    configWebMap: WebMap | null | undefined,
    restoreLabelSettings: (labels: string[], labelsEnabled: boolean) => void,
    basemapGalleryVM: BasemapGalleryViewModel | null
) => {
    const mapview = getMapView();
    if (!mapview?.map) return;

    const mapSettings = getMapSettings();
    if (!mapSettings) return;

    if (mapSettings.enabledLayers.length > 0) {
        // First hide all layers
        mapview.map.allLayers.forEach((layer) => {
            if (
                layer.type !== "graphics" &&
                layer.listMode !== "hide" &&
                layer.type !== "vector-tile" &&
                layer.type !== "elevation" &&
                layer.type !== "imagery-tile" &&
                layer.type !== "web-tile"
            ) {
                layer.visible = false;
            }
        });
        // Then enable stored layers
        const layersNotFound: LayerSetting[] = [];
        for (const layerSetting of mapSettings.enabledLayers) {
            // This is a single layer or the child of parents
            // Step 1 check if the layer is in mapview already
            const existingLayer = mapview.map.allLayers.find((l) => {
                const layerUrl = (l as any).url ?? "";
                return l.title === layerSetting.title && layerUrl === layerSetting.url;
            });
            if (existingLayer) {
                existingLayer.visible = true;
                (existingLayer as any).opacity = layerSetting.opacity;
                (existingLayer as any).minScale = layerSetting.zoomLevel;
                setAllParentsVisible(existingLayer);
                continue; // proceed to next layerSetting
            }

            // Step 2: try to find the layer in configmap
            let layerToEnable: Layer | null = null;
            if (configWebMap) {
                layerToEnable =
                    configWebMap.allLayers.find((l) => {
                        const layerUrl = (l as any).url ?? "";
                        return l.title === layerSetting.title && layerUrl === layerSetting.url;
                    }) || null;
            }
            if (layerToEnable) {
                layerToEnable.visible = true;
                (layerToEnable as any).opacity = layerSetting.opacity;
                (layerToEnable as any).minScale = layerSetting.zoomLevel;
                await addUpmostParentToMap(layerToEnable);
                hideAllChildrenOfLayer(layerToEnable);
                showChildAndParentLayers(layerToEnable);
                continue; // proceed to next layerSetting
            }

            // Step 3: try to find the layer in theme maps
            for (const themeMap of themeMaps) {
                layerToEnable =
                    themeMap.allLayers.find((l) => {
                        const layerUrl = (l as any).url ?? "";
                        return l.title === layerSetting.title && layerUrl === layerSetting.url;
                    }) || null;
                if (layerToEnable) {
                    layerToEnable.visible = true;
                    (layerToEnable as any).opacity = layerSetting.opacity;
                    (layerToEnable as any).minScale = layerSetting.zoomLevel;
                    await addUpmostParentToMap(layerToEnable);
                    hideAllChildrenOfLayer(layerToEnable);
                    showChildAndParentLayers(layerToEnable);
                    break; // exit themeMaps loop
                }
            }

            if (!layerToEnable) {
                // Store for later processing
                layersNotFound.push(layerSetting);
            }
        }

        // Try to enable layers not found yet by adding them directly from their URLs
        if (layersNotFound.length > 0) {
            // Helper to create layer from LayerSetting
            const createLayerFromSetting = async (setting: LayerSetting): Promise<Layer | null> => {
                try {
                    let layer: Layer | null = null;

                    switch (setting.layerType) {
                        case "feature": {
                            const FeatureLayer = await import("@arcgis/core/layers/FeatureLayer.js");
                            if (setting.url) {
                                layer = new FeatureLayer.default({
                                    url: setting.url,
                                    title: setting.title,
                                    opacity: setting.opacity,
                                    minScale: setting.zoomLevel,
                                });
                            }
                            break;
                        }
                        case "map-image": {
                            const MapImageLayer = await import("@arcgis/core/layers/MapImageLayer.js");
                            if (setting.url) {
                                layer = new MapImageLayer.default({
                                    url: setting.url,
                                    title: setting.title,
                                    opacity: setting.opacity,
                                    minScale: setting.zoomLevel,
                                });
                            }
                            break;
                        }
                        case "tile": {
                            const TileLayer = await import("@arcgis/core/layers/TileLayer.js");
                            if (setting.url) {
                                layer = new TileLayer.default({
                                    url: setting.url,
                                    title: setting.title,
                                    opacity: setting.opacity,
                                    minScale: setting.zoomLevel,
                                });
                            }
                            break;
                        }
                        case "group": {
                            layer = new GroupLayer({
                                title: setting.title,
                                opacity: setting.opacity,
                            });
                            break;
                        }
                        case "wms": {
                            if (setting.url) {
                                layer = new WMSLayer({
                                    url: setting.url,
                                    title: setting.title,
                                    opacity: setting.opacity,
                                    minScale: setting.zoomLevel,
                                });
                            }
                            break;
                        }
                        default: {
                            console.warn(`Unsupported layer type for restoration: ${setting.layerType}`);
                            break;
                        }
                    }

                    if (layer) {
                        await layer.load();
                    }
                    return layer;
                } catch (error) {
                    console.error(`Failed to create layer ${setting.title}:`, error);
                    return null;
                }
            };

            // Process layers in order: first parents (those without parent), then children
            const rootLayers = layersNotFound.filter((ls) => ls.parent === null);

            for (const rootSetting of rootLayers) {
                const rootLayer = await createLayerFromSetting(rootSetting);
                if (rootLayer) {
                    mapview.map.add(rootLayer);

                    // Now add children
                    const children = layersNotFound.filter((ls) => ls.parent === rootSetting);
                    for (const childSetting of children) {
                        const childLayer = await createLayerFromSetting(childSetting);
                        if (childLayer && rootLayer instanceof GroupLayer) {
                            rootLayer.add(childLayer);

                            // Handle grandchildren recursively
                            const grandchildren = layersNotFound.filter((ls) => ls.parent === childSetting);
                            if (grandchildren.length > 0 && childLayer instanceof GroupLayer) {
                                for (const grandchildSetting of grandchildren) {
                                    const grandchildLayer = await createLayerFromSetting(grandchildSetting);
                                    if (grandchildLayer) {
                                        childLayer.add(grandchildLayer);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // Loading basemap last to give basemapgalleryVM time to initialize
        if (mapSettings.basemap.length > 0) {
            const basemapLayers: Layer[] = [];
            const referenceLayers: Layer[] = [];

            mapSettings.basemap.forEach((layerInfo) => {
                switch (layerInfo.layerType) {
                    case "vector-tile": {
                        const vectorTileLayer = new VectorTileLayer({ url: layerInfo.url });
                        if (layerInfo.type === "base") {
                            basemapLayers.push(vectorTileLayer);
                        } else {
                            referenceLayers.push(vectorTileLayer);
                        }
                        break;
                    }
                    case "imagery-tile": {
                        const imageryTileLayer = new ImageryTileLayer({ url: layerInfo.url });
                        if (layerInfo.type === "base") {
                            basemapLayers.push(imageryTileLayer);
                        } else {
                            referenceLayers.push(imageryTileLayer);
                        }
                        break;
                    }
                    case "web-tile": {
                        const webTileLayer = new WebTileLayer({ urlTemplate: layerInfo.url });
                        if (layerInfo.type === "base") {
                            basemapLayers.push(webTileLayer);
                        } else {
                            referenceLayers.push(webTileLayer);
                        }
                        break;
                    }
                    // Add cases for other layer types as needed
                    default: {
                        console.warn(`Unsupported layer type: ${layerInfo.layerType}`);
                        break;
                    }
                }
            });

            const newBasemap = new Basemap({
                baseLayers: basemapLayers,
                referenceLayers: referenceLayers,
            });

            await newBasemap.load();

            if (basemapGalleryVM) {
                if (basemapGalleryVM.state !== "ready") {
                    // Wait for the basemap gallery to be ready
                    await new Promise<void>((resolve) => {
                        const handle = basemapGalleryVM.watch("state", (newValue) => {
                            if (newValue === "ready") {
                                handle.remove();
                                resolve();
                            }
                        });
                    });
                }
                // I don't know why states are ready and loaded but setting activeBasemap fails without this delay
                await new Promise<void>((resolve) => {
                    setTimeout(() => {
                        resolve();
                    }, 200);
                });

                //suddenly stopped working
                basemapGalleryVM.activeBasemap = newBasemap;
            }
        }
    }

    // Always restore the label selection, even when labels are toggled off, so the user can
    // switch them back on without reconfiguring the fields.
    restoreLabelSettings(mapSettings.enabledLabels, mapSettings.labelsEnabled);
};

export const hideAllChildrenOfLayer = (layer: Layer) => {
    if (layer instanceof GroupLayer) {
        layer.layers.forEach((sublayer) => {
            sublayer.visible = false;
            // Recursively hide children
            hideAllChildrenOfLayer(sublayer);
        });
    }
};

export const showChildAndParentLayers = (layer: Layer) => {
    layer.visible = true;
    // Show all parent layers
    setAllParentsVisible(layer);
};
